import { computed } from 'vue';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import { storeToRefs } from 'pinia';
import { routeChangeStatus } from '../router';
import { getSegmentName } from '../utils/getSegmentName';
import { useJssStore } from '../stores/jssStore';

// Singleton instance
let instance = null;

/**
 * Returns standardized base properties for data layer
 *
 * @returns {Object} Computed properties for data layer (singleton)
 */
export const useBaseDataLayerVariables = () => {
  if (instance) return instance;

  const jssStore = useJssStore();
  const {
    canonicalUrl,
    venueName,
    venueId,
    brandName,
    segment,
    isHotel,
    sports,
    division,
    subDivision,
    subSegment,
    siteVirtualFolder,
    venueOrigin,
  } = storeToRefs(jssStore);

  const pageURL = computed(() => {
    if (isServer())
      return {
        previous: '',
        current: '',
        currentFullUrl: canonicalUrl.value || '/',
      };

    const previous = routeChangeStatus.previousInternalRoute
      ? window.location.origin + routeChangeStatus.previousInternalRoute
      : '';
    const current = routeChangeStatus.currentInternalRoute || '';

    return {
      previous: previous || document.referrer || '',
      current: current || window.location.href || '',
      currentFullUrl: window.location.href.split('?')[0],
    };
  });

  // Standard base properties for all events
  const baseProps = computed(() => {
    return {
      brand_name: brandName.value || '',
      brand_segment: getSegmentName(segment.value, brandName.value, venueId.value),
      division: division.value || 'No Division',
      sub_division: subDivision.value || '',
      sub_brand_segment: subSegment.value || '',
      house_name: venueName.value || '',
      house_id: venueId.value || '',
      hotel_flag: isHotel.value ?? true,
      sports_flag: sports.value ?? false,
      previous_page_url: pageURL.value.previous,
    };
  });

  // Additional properties for page view events
  const pageProps = computed(() => {
    return {
      full_url: pageURL.value.currentFullUrl,
    };
  });

  // Link properties for tracking clicks
  const linkProps = computed(() => {
    return {
      link_page_path: pageURL.value.current,
      link_domain: venueOrigin.value || '',
    };
  });

  // Item-level properties for ecommerce events
  const itemProps = computed(() => {
    const pathSegments = siteVirtualFolder.value?.split('/') || [];

    return {
      item_id: venueId.value || '',
      item_brand: segment.value || '',
      affiliation: venueName.value || '',
      item_category2: pathSegments[1] || '',
      item_category3: pathSegments[2] || '',
    };
  });

  // Promotion item properties for promotion related events
  const promoItemProps = computed(() => ({
    location_id: pageURL.value.currentFullUrl,
  }));

  instance = {
    baseProps,
    pageProps,
    linkProps,
    itemProps,
    promoItemProps,
  };
  return instance;
};
